(() => {
  const lb = document.getElementById('lm-lightbox');
  if (!lb) return;

  const stage    = lb.querySelector('[data-lm-stage]');
  const imgEl    = lb.querySelector('.lm-lightbox__img');
  const spinner  = lb.querySelector('.lm-lightbox__spinner');
  const btnIn    = lb.querySelector('[data-lm-zoom-in]');
  const btnOut   = lb.querySelector('[data-lm-zoom-out]');
  const btn1x    = lb.querySelector('[data-lm-zoom-reset]'); // 1:1 toggle
  const btnFit   = lb.querySelector('[data-lm-zoom-fit]');
  const closers  = lb.querySelectorAll('[data-lm-close], .lm-lightbox__backdrop');

  // Block right-click/drag in lightbox
  [stage, imgEl].forEach(el => el.addEventListener('contextmenu', e => e.preventDefault()));
  imgEl.setAttribute('draggable', 'false');
  imgEl.addEventListener('dragstart', e => e.preventDefault());

  // --- Pan/zoom state ---
  let scale = 1, minScale = 1, maxScale = 8;
  let x = 0, y = 0;                // pan offsets (px), relative to centered image
  let isOpen = false;
  let lastActive = null;
  let userInteracted = false;
  const EPS = 0.01;

  // --- Single / multi pointer state ---
  let startPtr = null, startX = 0, startY = 0; // single-finger pan
  const activePtrs = new Map();                // id -> {x,y}
  let pinchStartDist = 0, pinchStartScale = 1;

  // --- Inertia (momentum) state ---
  let vx = 0, vy = 0;          // px per frame-ish
  let lastMoveTS = 0;          // timestamp for velocity calc
  let inertiaRAF = null;
  const INERTIA_FRICTION = 0.92;  // 0.90–0.95 feels nice
  const INERTIA_MIN_SPEED = 0.15; // stop threshold
  const MAX_FRAME_DT = 40;        // ms cap to avoid spikes
  const OVERSCROLL = 24;          // px cushion beyond hard bounds

  /* ----------------- helpers ----------------- */
  function apply() {
    imgEl.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale})`;
  }

  function fit() {
    if (!imgEl.naturalWidth || !imgEl.naturalHeight) return;
    const r = stage.getBoundingClientRect();
    minScale = Math.min(r.width / imgEl.naturalWidth, r.height / imgEl.naturalHeight);
    scale = minScale;
    x = 0; y = 0;
    stopInertia();
    apply();
  }

  function zoomAt(f, cx, cy) {
    const prev = scale;
    const next = Math.min(maxScale, Math.max(minScale, scale * f));
    if (Math.abs(next - prev) < EPS) return;

    // On zoom, re-center around the focal point
    const rect = stage.getBoundingClientRect();
    const px = cx - rect.left - rect.width / 2 - x;
    const py = cy - rect.top  - rect.height / 2 - y;
    const k = next / prev - 1;

    scale = next;
    x -= px * k; y -= py * k;

    clampPan();
    stopInertia();
    apply();

    if (scale > minScale + EPS) userInteracted = true;
  }

  // Jump to actual pixels (scale=1) while keeping center under cursor
  function setActualSize(cx = window.innerWidth / 2, cy = window.innerHeight / 2) {
    const prev = scale;
    const target = 1;
    if (Math.abs(target - prev) < EPS) return;

    const rect = stage.getBoundingClientRect();
    const px = cx - rect.left - rect.width  / 2 - x;
    const py = cy - rect.top  - rect.height / 2 - y;
    const k = target / prev - 1;

    scale = Math.min(Math.max(target, minScale), maxScale); // respect minScale
    x -= px * k; y -= py * k;

    clampPan();
    stopInertia();
    apply();
    userInteracted = true;
  }

  function preload(src) {
    return new Promise((resolve, reject) => {
      if (!src) { reject(); return; }
      const im = new Image();
      im.onload = () => resolve(src);
      im.onerror = reject;
      im.src = src;
    });
  }

  // Choose best hi-res source from slide attributes
  function pickZoomSrcFromSlide(slide, fallback) {
    const z1600 = slide.getAttribute('data-lm-zoom-1600');
    const z2400 = slide.getAttribute('data-lm-zoom-2400');
    const z3200 = slide.getAttribute('data-lm-zoom-3200');
    if (z1600 || z2400 || z3200) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const longest = Math.max(window.innerWidth, window.innerHeight) * dpr;
      if (longest <= 1800) return z1600 || z2400 || z3200 || fallback;
      if (longest <= 2600) return z2400 || z3200 || z1600 || fallback;
      return z3200 || z2400 || z1600 || fallback;
    }
    const img = slide.querySelector('img');
    return slide.getAttribute('data-lm-zoom-src')
        || (img && img.getAttribute('data-lm-zoom-src'))
        || fallback;
  }

  // --- Compute clamping bounds so the image can't be flung off-screen ---
  // Returns {minX, maxX, minY, maxY}
  function computePanBounds() {
    const r = stage.getBoundingClientRect();
    const imgW = imgEl.naturalWidth * scale;
    const imgH = imgEl.naturalHeight * scale;

    // The image is centered at (0,0). We want to limit x/y so that the image still covers the stage.
    // Half sizes:
    const halfStageW = r.width / 2;
    const halfStageH = r.height / 2;
    const halfImgW = imgW / 2;
    const halfImgH = imgH / 2;

    // If the image is smaller than stage in a dimension, no need to pan in that axis (lock to 0).
    // If bigger, allow panning so edges line up with stage edges (with small overscroll).
    let minX, maxX, minY, maxY;

    if (halfImgW <= halfStageW) {
      minX = maxX = 0;
    } else {
      const limit = halfImgW - halfStageW;
      minX = -limit - OVERSCROLL;
      maxX =  limit + OVERSCROLL;
    }

    if (halfImgH <= halfStageH) {
      minY = maxY = 0;
    } else {
      const limit = halfImgH - halfStageH;
      minY = -limit - OVERSCROLL;
      maxY =  limit + OVERSCROLL;
    }

    return { minX, maxX, minY, maxY };
  }

  function clampPan() {
    const { minX, maxX, minY, maxY } = computePanBounds();
    x = Math.max(minX, Math.min(maxX, x));
    y = Math.max(minY, Math.min(maxY, y));
  }

  // --- Inertia motion after pan release ---
  function startInertia() {
    stopInertia();
    let last = performance.now();
    const step = (now) => {
      inertiaRAF = requestAnimationFrame(step);
      let dt = now - last;
      last = now;
      if (dt > MAX_FRAME_DT) dt = MAX_FRAME_DT;

      // Apply velocity
      x += vx * (dt / 16);
      y += vy * (dt / 16);

      // Clamp to bounds; if we hit a boundary, dampen velocity (basic "bounce stop")
      const prevX = x, prevY = y;
      clampPan();
      if (Math.abs(x - prevX) > 0.5) vx *= 0.3;
      if (Math.abs(y - prevY) > 0.5) vy *= 0.3;

      // Friction
      vx *= INERTIA_FRICTION;
      vy *= INERTIA_FRICTION;

      apply();

      // Stop when speed low or we’re effectively stationary
      if (Math.hypot(vx, vy) < INERTIA_MIN_SPEED) {
        stopInertia();
      }
    };
    inertiaRAF = requestAnimationFrame(step);
  }

  function stopInertia() {
    if (inertiaRAF) {
      cancelAnimationFrame(inertiaRAF);
      inertiaRAF = null;
    }
  }

  /* ------------- open/close with placeholder + spinner ------------- */
  function openWithPlaceholder(placeholderSrc, zoomSrc, alt) {
    document.body.classList.add('lm-no-scroll');
    lastActive = document.activeElement;

    // Reset transforms/state on open
    scale = 1; x = 0; y = 0; userInteracted = false;
    vx = 0; vy = 0; stopInertia();

    lb.classList.add('is-loading');
    if (spinner) spinner.hidden = false;

    imgEl.style.opacity = 0;
    imgEl.src = placeholderSrc;
    imgEl.alt = alt || '';
    imgEl.onload = () => { fit(); imgEl.style.opacity = 1; };

    lb.hidden = false;
    lb.setAttribute('aria-hidden', 'false');
    isOpen = true;
    lb.querySelector('.lm-lightbox__dialog').focus();

    preload(zoomSrc).then(() => {
      const prevScale = scale, prevX = x, prevY = y;
      const hadUserZoom = userInteracted;

      imgEl.style.opacity = 0;
      imgEl.onload = () => {
        fit();
        if (hadUserZoom && prevScale > minScale + EPS) {
          scale = Math.min(Math.max(prevScale, minScale), maxScale);
          x = prevX; y = prevY; clampPan(); apply();
        }
        lb.classList.remove('is-loading');
        if (spinner) spinner.hidden = true;
        imgEl.style.opacity = 1;
      };
      imgEl.src = zoomSrc;
    }).catch(() => {
      lb.classList.remove('is-loading');
      if (spinner) spinner.hidden = true;
    });
  }

  function close() {
    isOpen = false;
    lb.hidden = true;
    lb.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lm-no-scroll');
    imgEl.src = '';
    if (spinner) spinner.hidden = true;
    lb.classList.remove('is-loading');
    userInteracted = false;

    // reset multi-touch + inertia
    activePtrs.clear(); pinchStartDist = 0; pinchStartScale = 1;
    vx = 0; vy = 0; stopInertia();

    if (lastActive && lastActive.focus) lastActive.focus();
  }

  /* ----------------- event wiring ----------------- */

  // Open on gallery click
  document.addEventListener('click', (e) => {
    const slide = e.target.closest('li[data-media-type="image"]');
    if (!slide) return;
    if (!slide.closest('.product__media-list')) return;
    e.preventDefault(); e.stopPropagation();

    const img = slide.querySelector('img');
    if (!img) return;

    const placeholderSrc = img.currentSrc || img.src;
    const alt = img.getAttribute('alt') || '';
    const zoomSrc = pickZoomSrcFromSlide(slide, placeholderSrc);

    openWithPlaceholder(placeholderSrc, zoomSrc, alt);
  }, true);

  // Buttons
  if (btnIn)  btnIn .addEventListener('click', () => { stopInertia(); zoomAt(1.25, window.innerWidth/2, window.innerHeight/2); });
  if (btnOut) btnOut.addEventListener('click', () => { stopInertia(); zoomAt(1/1.25, window.innerWidth/2, window.innerHeight/2); });
  if (btnFit) btnFit.addEventListener('click', () => { fit(); userInteracted = false; });
  if (btn1x)  btn1x .addEventListener('click', () => {
    if (Math.abs(scale - minScale) < EPS) setActualSize();
    else { fit(); userInteracted = false; }
  });
  closers.forEach(el => el.addEventListener('click', close));

  // Mouse wheel zoom
  stage.addEventListener('wheel', (e) => {
    if (!isOpen) return;
    e.preventDefault();
    stopInertia();
    zoomAt(e.deltaY < 0 ? 1.1 : 1/1.1, e.clientX, e.clientY);
  }, { passive: false });

  /* -------- Single- & Multi-touch with inertia -------- */
  stage.addEventListener('pointerdown', (e) => {
    // Cancel inertia on new interaction
    stopInertia();

    // Track this pointer for possible pinch
    activePtrs.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePtrs.size === 1) {
      // Pan start
      stage.setPointerCapture(e.pointerId);
      startPtr = { id: e.pointerId, x: e.clientX, y: e.clientY };
      startX = x; startY = y;
      if (scale > minScale + EPS) userInteracted = true;
      vx = 0; vy = 0; lastMoveTS = performance.now();
    } else if (activePtrs.size === 2) {
      // Pinch start
      const pts = Array.from(activePtrs.values());
      pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchStartScale = scale;
      // cancel single-finger pan while pinching
      startPtr = null;
    }
  });

  stage.addEventListener('pointermove', (e) => {
    if (!activePtrs.has(e.pointerId)) return;
    const now = performance.now();
    const prev = activePtrs.get(e.pointerId);
    activePtrs.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePtrs.size === 2 && pinchStartDist > 0) {
      // Pinch zoom around midpoint
      const pts = Array.from(activePtrs.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (dist > 0) {
        const target = Math.min(maxScale, Math.max(minScale, pinchStartScale * (dist / pinchStartDist)));
        const prevScale = scale;
        if (Math.abs(target - prevScale) > EPS) {
          // midpoint in client coords
          const midX = (pts[0].x + pts[1].x) / 2;
          const midY = (pts[0].y + pts[1].y) / 2;
          zoomAt(target / prevScale, midX, midY);
          userInteracted = true;
        }
      }
    } else if (startPtr && startPtr.id === e.pointerId) {
      // Single-finger pan
      const dx = e.clientX - startPtr.x;
      const dy = e.clientY - startPtr.y;

      // velocity (px per frame-ish) from last move
      const dt = Math.max(1, Math.min(MAX_FRAME_DT, now - lastMoveTS));
      vx = (dx - (x - startX)) / (dt / 16);
      vy = (dy - (y - startY)) / (dt / 16);
      lastMoveTS = now;

      x = startX + dx;
      y = startY + dy;
      clampPan();
      apply();
    }
  });

  function endPointer(e) {
    // If ending a pan, start inertia with current velocity
    if (startPtr && startPtr.id === e.pointerId) {
      startPtr = null;
      // Only fling if we're zoomed in and moving
      if (scale > minScale + EPS && Math.hypot(vx, vy) > INERTIA_MIN_SPEED) {
        startInertia();
      } else {
        vx = 0; vy = 0;
      }
    }

    activePtrs.delete(e.pointerId);
    if (activePtrs.size < 2) {
      pinchStartDist = 0;
    }
  }
  stage.addEventListener('pointerup', endPointer);
  stage.addEventListener('pointercancel', endPointer);
  stage.addEventListener('pointerleave', endPointer);

  // Double-tap / double-click: Fit ↔ ~2x
  let lastTap = 0;
  stage.addEventListener('click', (e) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      e.preventDefault();
      stopInertia();
      if (Math.abs(scale - minScale) < EPS) { zoomAt(2 / scale, window.innerWidth/2, window.innerHeight/2); }
      else { fit(); userInteracted = false; }
    }
    lastTap = now;
  });

  // Esc to close, refit on resize
  document.addEventListener('keydown', (e) => { if (isOpen && e.key === 'Escape') close(); });
  window.addEventListener('resize', () => { if (isOpen) fit(); });

  /* --------- proactive preloading for snappiness --------- */
  function primePreloadFromTarget(target) {
    const slide = target && target.closest && target.closest('li[data-media-type="image"]');
    if (!slide || slide._lmPreloaded) return;
    const img = slide.querySelector('img');
    if (!img) return;
    const ph = img.currentSrc || img.src;
    const zoomSrc = pickZoomSrcFromSlide(slide, ph);
    if (!zoomSrc || zoomSrc === ph) return;
    slide._lmPreloaded = true;
    preload(zoomSrc).catch(() => {});
  }
  document.addEventListener('mouseenter', (e) => {
    if (window.matchMedia('(pointer:fine)').matches) primePreloadFromTarget(e.target);
  }, true);
  document.addEventListener('touchstart', (e) => { primePreloadFromTarget(e.target); }, { passive: true });

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) primePreloadFromTarget(entry.target); });
    }, { rootMargin: '200px' });
    document.querySelectorAll('li[data-media-type="image"]').forEach(li => io.observe(li));
  }


/* --- Luminous Made: custom gallery lightbox bridge (click icon or image) --- ONLY FOR CUSTOM PRODUCT GALLERY */
document.addEventListener('click', (e) => {
  // Allow click on either the zoom icon OR the image itself
  const trigger = e.target.closest('[data-lm-lightbox-trigger], .lm-image, .lm-d-image');
  if (!trigger) return;

  const slide = trigger.closest('[data-lm-lightbox]');
  if (!slide) return;

  const img = slide.querySelector('.lm-image, .lm-d-image');
  if (!img) return;

  const placeholderSrc = img.currentSrc || img.src;
  const alt = img.getAttribute('alt') || '';

  // Use your existing zoom source logic if present
  const zoomSrc = (typeof pickZoomSrcFromSlide === 'function')
    ? pickZoomSrcFromSlide(slide, placeholderSrc)
    : img.getAttribute('data-lm-zoom-src') || placeholderSrc;

  // Use the original lightbox open function
  if (typeof openWithPlaceholder === 'function') {
    openWithPlaceholder(placeholderSrc, zoomSrc, alt);
  }
});


})();
